import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, AbstractControl } from '@angular/forms';
import { RequiredValidator } from 'src/app/customValidators/required/required';
import { ResourceManagerService } from 'src/app/services/resourceManager/resource-manager.service';
import { LOV_KEYS, HRCHY_KEYS } from 'src/app/dataModels/lovs';
import { CONSTANTS } from 'src/app/dataModels/constants';
import { Regex } from 'src/app/dataModels/regex';
import { HelperService } from 'src/app/services/utils/helper.service';
import { CanComponentDeactivate } from 'src/app/can-deactivate.guard';
import { FacilityService } from '../../facility/facility.service';
import { CONFIG } from 'src/app/dataModels/config';

/**
 * Address component
 */
@Component({
  selector: 'app-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss']
})
export class AddressComponent implements OnInit, CanComponentDeactivate, OnChanges {
  /**
   * Form group for Address
   */
  addressFrm: UntypedFormGroup;

  /**
   * Mode of Address ADD, UPDATE
   */
  @Input() mode;

  /**
   * Id
   */
  @Input() id;

  /**
   * IdSeq
   */
  @Input() idSeq;

  /**
   * boolean indicating if address component has to be refreshed
   */
  @Input() refresh;

  /**
   * List of States
   */
  stateList = [];

  /**
   * List of counties
   */
  countyList = [];

  /**
   * List of municipalities
   */
  municipalityList = [];

  /**
   * Heirarchy of County Municipality list
   */
  countyMuniList = [];

  /**
   * boolean indicator to indicate form is submitted, and to run validations
   */
  isSubmit = false;

  /**
   * boolean indicator to indicate if form has to be displayed or skeleton
   */
  showData = false;

  /**
   * Form data for address fields
   */
  addressFormData = {
    isError: false,
    hasData: false,
    data: {} as any,
    origData: {} as any,
    isDataModified: false
  };

  /**
   * boolean indicator to indicate if County has to be disabled
   */
  isCountyDisabled = false;

  /**
   * boolean indicator to indicate if Municipality has to be disabled
   */
  isMunicipalityDisabled = false;

  /**
   * boolean indicator to indicate if any error
   */
  isError;

  /**
   * address info
   */
  addressInfo;

  /**
   * County name to be displayed during Review
   */
  countyDescr;

  /**
   * Municipality name to be displayed during Review
   */
  municipalityDescr;

  /**
   * State name to be displayed during Review
   */
  stateDescr;

  /**
   * boolean indicator indicating if form will be disabled
   */
  @Input() disableForm = false;

  /**
   * Constructor to inject dependency
   * @param resourceService Instance of resourceManagerService
   * @param helperService  Instance of helperService
   * @param facilityService Instance of facilityService
   */
  constructor(
    private resourceService: ResourceManagerService,
    private helperService: HelperService,
    private facilityService: FacilityService
  ) {}

  /**
   * Initialize component
   */
  ngOnInit() {
    this.initFormControls();
    this.getLovs();
    this.getCountyHierarchy();
  }

  /**
   * Initialize form controls
   */
  initFormControls() {
    this.addressFrm = new UntypedFormGroup({
      addressLine1: new UntypedFormControl('', [
        Validators.required,
        RequiredValidator.validateRequired,
        Validators.minLength(3),
        Validators.maxLength(35),
        Validators.pattern(Regex.ADDRESS_LINE_REGEX)
      ]),
      addressLine2: new UntypedFormControl('', [Validators.minLength(3), Validators.maxLength(35), Validators.pattern(Regex.ADDRESS_LINE_REGEX)]),
      city: new UntypedFormControl('', [
        Validators.required,
        RequiredValidator.validateRequired,
        Validators.minLength(3),
        Validators.maxLength(34),
        Validators.pattern(Regex.ALPHA)
      ]),
      state: new UntypedFormControl('', [Validators.required]),
      zip: new UntypedFormControl('', [Validators.required,Validators.minLength(5), Validators.maxLength(10),Validators.pattern(Regex.ZIP)]),
      countyCode: new UntypedFormControl('', [Validators.required]),
      municipalityCode: new UntypedFormControl('', [Validators.required])
    });
  }

  /**
   * Get resources for State and CountyMuniHeirarchy
   * 1.LOV_KEYS.STATE2
   * 2.HRCHY_KEYS.COUNTY_MUNI
   */
  getLovs() {
    const resourceList = [
      {
        key: LOV_KEYS.STATE,
        type: CONSTANTS.LOV
      }
    ];

    this.resourceService.getResources(resourceList).then(response => {
      this.stateList = response[LOV_KEYS.STATE];
      this.stateChange();
    });
  }

  getCountyHierarchy() {
    const request = 
      {
        "key": HRCHY_KEYS.COUNTY_MUNI
      };
      
    this.resourceService.getMSResources(request,CONFIG.MS_GET_HIERARCHY).then((response) => {
      this.countyMuniList = this.helperService.getActiveCountyAndMunicipalityList(response.json);
      this.countyChange();
    });
  }

  /**
   * getter for address form controls
   */
  get f() {
    return this.addressFrm?.controls;
  }

  /**
   * Check if control is invalid
   * @param ctrl ctrl to check invalidity for
   */
  isControlInvalid(ctrl: AbstractControl) {
    return this.helperService.isControlInvalid(this.isSubmit, ctrl);
  }

  /**
   * On state change, enable, disable county, municipality controls
   * 1. If state is not NJ, clear County and Municipality and disable County and Municipality
   * 2. If State is NJ, clear County and Municipality and enable County and Municipality
   */
  stateChange() {
    const selectedState = this.addressFrm.value.state ? this.addressFrm.value.state : this.f.state.value;

    this.stateDescr = this.resourceService.getLovColumnProperties(LOV_KEYS.STATE, selectedState, 'descr');

    if (!this.helperService.IsNullOrEmpty(selectedState)) {
      if (selectedState !== 'NJ') {
        this.modifyCountyMuniControls('0000', true);
      } else {
        this.modifyCountyMuniControls('', false);
      }
    } else {
      this.modifyCountyMuniControls('', true);
    }

    this.doDisableForm();
  }

  /**
   * On county change, re-load Municipality List with selected county
   */
  countyChange() {
    const selectedCountyCode = this.f.countyCode.value || '0000';
    if(selectedCountyCode == '0000'){
      this.addressFrm.controls.municipalityCode.setValue('0000');
    }
    const matchedCounty = this.countyMuniList?.find(county => county.countyCode === selectedCountyCode);

    if (matchedCounty) {
      this.countyDescr = matchedCounty.countyCode === '0000' ? "" : matchedCounty.countyName;
      this.municipalityList = matchedCounty.municipality;
    }
  }

  /**
   * Set values and enable/disable County and Municipality
   * @param value value to set to County and Municipality
   * @param isDisable to enable or disable County and Municipality
   */
  modifyCountyMuniControls(value, isDisable) {
    this.addressFrm.controls.countyCode.setValue(value);
    this.countyChange();
    this.addressFrm.controls.municipalityCode.setValue(value);
    if (isDisable) {
      this.addressFrm.controls.countyCode.disable();
      this.addressFrm.controls.municipalityCode.disable();
    } else {
      this.addressFrm.controls.countyCode.enable();
      this.addressFrm.controls.municipalityCode.enable();
    }
  }

  /**
   * Get address form values
   */
  getFormData() {
    this.isSubmit = true;
    this.addressFormData.isError = !(this.addressFrm.valid || this.addressFrm.status === 'DISABLED');
    if (this.addressFrm.valid) {
      if(this.addressFrm.controls.state.value){
      this.addressFrm.value.state = this.addressFrm.controls.state.value;
      this.addressFrm.value.countyCode = this.addressFrm.controls.countyCode.value;
      this.addressFrm.value.municipalityCode = this.addressFrm.controls.municipalityCode.value;
      }
      if (this.mode === CONSTANTS.UPDATE && this.addressInfo) {
        let isFormModified = false;
        for (const prop in this.addressFrm.value) {
          if (this.addressFrm.value.hasOwnProperty(prop)) {
            if (this.addressFrm.value[prop] !== this.addressInfo[prop]) {
              isFormModified = true;
              break;
            }
          }
        }
        this.addressFormData.hasData = isFormModified;
        this.addressFormData.isDataModified = isFormModified;
        if (isFormModified) {
          this.addressFormData.data = this.addressFrm.value;
          this.addressFormData.origData = this.addressInfo;
        } else {
          this.addressFormData.data = {};
          this.addressFormData.origData = {};
        }
      } else {
        this.addressFormData.hasData = true;
        this.addressFormData.isDataModified = true;
        this.addressFormData.data = this.addressFrm.value;
      }
    } else {
      this.addressFormData.hasData = false;
      this.addressFormData.data = {};
    }

    return this.addressFormData;
  }

  /**
   * Get address form values
   */
  getFormDataToCopy() {
    this.isSubmit = true;
    this.addressFormData.isError = !(this.addressFrm.valid || this.addressFrm.status === 'DISABLED');
    if (this.addressFrm.valid) {
      if(this.addressFrm.controls.state.value){
      this.addressFrm.value.state = this.addressFrm.controls.state.value;
      this.addressFrm.value.countyCode = this.addressFrm.controls.countyCode.value;
      this.addressFrm.value.municipalityCode = this.addressFrm.controls.municipalityCode.value;
      }
      if (this.mode === CONSTANTS.UPDATE && this.addressInfo) {
        let isFormModified = false;
        for (const prop in this.addressFrm.value) {
          if (this.addressFrm.value.hasOwnProperty(prop)) {
            if (this.addressFrm.value[prop] !== this.addressInfo[prop]) {
              isFormModified = true;
              break;
            }
          }
        }
          this.addressFormData.data = this.addressFrm.value;
          this.addressFormData.origData = this.addressInfo;
      } 
    } else {
      this.addressFormData.hasData = false;
      this.addressFormData.data = {};
    }

    return this.addressFormData;
  }

  /**
   * Reset address data to its original value
   */
  resetAddressData() {
    this.isSubmit = false;
    this.helperService.resetForm(this.addressFrm);
    if (this.mode === CONSTANTS.UPDATE) {
      this.setFormData();
    }
  }

  /**
   * Determine if the component can deactivate
   */
  canDeactivate() {
    return this.helperService.canDeactivate(this.addressFrm);
  }

  /**
   * determine to refresh the address data
   * @param changes changes indicating what values are changed
   */
  ngOnChanges(changes: SimpleChanges) {
    this.showData = changes.refresh?.currentValue;
  }

  /**
   * Set address data
   * @param addressInfo addressInfo containing address data
   */
  setData(addressInfo) {
    console.log(addressInfo);
    this.addressInfo = addressInfo;
    this.showData = true;
    this.setFormData();
  }

  /**
   * Set address form values with the address data
   */
  setFormData() {
    this.addressFrm.controls.addressLine1.setValue(this.addressInfo?.addressLine1 ? this.addressInfo?.addressLine1:"");
    this.addressFrm.controls.addressLine2.setValue(this.addressInfo?.addressLine2 ? this.addressInfo?.addressLine2:"");
    this.addressFrm.controls.city.setValue(this.addressInfo?.city ? this.addressInfo?.city:"");
    this.addressFrm.controls.state.setValue(this.addressInfo?.state ? this.addressInfo?.state:"");
    this.addressFrm.controls.state.updateValueAndValidity();
    this.stateChange();
    this.addressFrm.controls.zip.setValue(this.addressInfo?.zip ? this.addressInfo?.zip:"");

    this.addressFrm.controls.countyCode.setValue(this.addressInfo?.countyCode ? this.addressInfo?.countyCode:"");
    this.countyChange();
    const matchedMunicipality = this.municipalityList.find(
      municipality => municipality.municipalityCode === this.addressInfo?.municipalityCode
    );
    if (matchedMunicipality?.municipalityCode) {
      this.addressFrm.controls.municipalityCode.setValue(this.addressInfo?.municipalityCode ? this.addressInfo?.municipalityCode:"");
      this.municipalityDescr = matchedMunicipality?.municipalityName;
    }
  }

    /**
   * Copy address form values with the address data
   */
    copyFormData(formData) {
      this.addressFrm.controls.addressLine1.setValue(formData?.addressLine1 ? formData?.addressLine1:"");
      this.addressFrm.controls.addressLine2.setValue(formData?.addressLine2 ? formData?.addressLine2:"");
      this.addressFrm.controls.city.setValue(formData?.city ? formData?.city:"");
      this.addressFrm.controls.state.setValue(formData?.state ? formData?.state:"");
      this.addressFrm.controls.state.updateValueAndValidity();
      this.stateChange();
      this.addressFrm.controls.zip.setValue(formData?.zip ? formData?.zip:"");
  
      this.addressFrm.controls.countyCode.setValue(formData?.countyCode ? formData?.countyCode:"");
      this.countyChange();
      const matchedMunicipality = this.municipalityList.find(
        municipality => municipality.municipalityCode === formData?.municipalityCode
      );
      if (matchedMunicipality?.municipalityCode) {
        this.addressFrm.controls.municipalityCode.setValue(formData?.municipalityCode ? formData?.municipalityCode:"");
        this.municipalityDescr = matchedMunicipality?.municipalityName;
      }
    }

  /**
   * Disable the form
   */
  doDisableForm() {
    if (this.disableForm) {
      this.addressFrm.disable();
    }
  }
}
